import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import API from "../api/api";

const money = (value) => `Gs ${Number(value || 0).toLocaleString("es-PY")}`;

const colors = {
  publicadas: "bg-moss",
  en_negociacion: "bg-amber-500",
  vendidas: "bg-emerald-500",
  cobradas: "bg-gold",
  vencidas: "bg-red-500",
};

function Bar({ label, value, max, color }) {
  const width = max > 0 ? Math.max(6, (Number(value || 0) / max) * 100) : 0;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="capitalize text-slate-600">{label.replace("_", " ")}</span>
        <span className="font-semibold text-primary">{value || 0}</span>
      </div>
      <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

function CashFlowBar({ item, max }) {
  const width = max > 0 ? Math.max(8, (Math.abs(Number(item.monto || 0)) / max) * 100) : 0;
  const isEntrada = item.tipo === "entrada";

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-stone-500">
        <span>{item.concepto}</span>
        <span>{money(item.monto)}</span>
      </div>
      <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${isEntrada ? "bg-emerald-500" : "bg-rose-500"}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function DashboardBackoffice() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    estados: null,
    intereses: [],
    flujo: [],
    morosidad: [],
    vencimientos: [],
    cartera: [],
  });

  const cargarDashboard = async () => {
    try {
      setLoading(true);
      setError("");

      const [estados, intereses, flujo, morosidad, vencimientos, cartera] =
        await Promise.all([
          API.get("/reportes/estados/"),
          API.get("/reportes/intereses/"),
          API.get("/reportes/flujo-caja/"),
          API.get("/reportes/morosidad/"),
          API.get("/reportes/vencimientos/"),
          API.get("/reportes/cuentas-por-cobrar/"),
        ]);

      setData({
        estados: estados.data,
        intereses: intereses.data || [],
        flujo: flujo.data || [],
        morosidad: morosidad.data || [],
        vencimientos: vencimientos.data || [],
        cartera: cartera.data || [],
      });
    } catch (err) {
      console.error("Error cargando dashboard backoffice:", err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          "No se pudo cargar el dashboard de reportes."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
  }, []);

  const metrics = useMemo(() => {
    const totalNominal = data.cartera.reduce(
      (acc, item) => acc + Number(item.monto_nominal || 0),
      0
    );
    const totalFinanciado = data.cartera.reduce(
      (acc, item) => acc + Number(item.monto_financiado || 0),
      0
    );
    const gananciaTotal = data.intereses.reduce(
      (acc, item) => acc + Number(item.ganancia_total || 0),
      0
    );
    const vencimientos30 = data.vencimientos.length;

    return { totalNominal, totalFinanciado, gananciaTotal, vencimientos30 };
  }, [data]);

  const estadoValues = data.estados
    ? {
        publicadas: data.estados.publicadas,
        en_negociacion: data.estados.en_negociacion,
        vendidas: data.estados.vendidas,
        cobradas: data.estados.cobradas,
        vencidas: data.estados.vencidas,
      }
    : {};
  const maxEstado = Math.max(1, ...Object.values(estadoValues).map(Number));
  const maxFlujo = Math.max(1, ...data.flujo.map((item) => Math.abs(Number(item.monto || 0))));

  if (loading) {
    return <p className="text-center text-primary mt-10">Cargando dashboard...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">Dashboard Backoffice</h1>
          <p className="text-slate-600">
            Vista visual de cartera, rentabilidad, vencimientos y riesgo del factoring.
          </p>
        </div>
        <Button className="bg-slate-700 hover:bg-slate-800" onClick={cargarDashboard}>
          Actualizar
        </Button>
      </div>

      {error && (
        <Card className="border border-red-200 bg-red-50 text-red-700">
          {error}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cream to-sand/50 border-white/50">
          <p className="text-sm text-stone-500">Cartera nominal</p>
          <p className="text-2xl font-bold text-primary">{money(metrics.totalNominal)}</p>
        </Card>
        <Card className="bg-gradient-to-br from-moss/15 to-cream border-moss/20">
          <p className="text-sm text-stone-500">Monto financiado</p>
          <p className="text-2xl font-bold text-emerald-700">{money(metrics.totalFinanciado)}</p>
        </Card>
        <Card className="bg-gradient-to-br from-gold/20 to-cream border-gold/30">
          <p className="text-sm text-stone-500">Ganancia estimada</p>
          <p className="text-2xl font-bold text-amber-700">{money(metrics.gananciaTotal)}</p>
        </Card>
        <Card className="bg-gradient-to-br from-stone-200 to-cream border-stone-300">
          <p className="text-sm text-stone-500">Vencimientos próximos</p>
          <p className="text-2xl font-bold text-rose-700">{metrics.vencimientos30}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="space-y-4">
          <h2 className="text-lg font-semibold text-primary">Facturas por Estado</h2>
          {Object.entries(estadoValues).map(([key, value]) => (
            <Bar
              key={key}
              label={key}
              value={value}
              max={maxEstado}
              color={colors[key] || "bg-stone-500"}
            />
          ))}
        </Card>

        <Card className="space-y-4">
          <h2 className="text-lg font-semibold text-primary">Flujo de Caja</h2>
          {data.flujo.length === 0 ? (
            <p className="text-sm text-slate-500">Sin movimientos de caja.</p>
          ) : (
            data.flujo.slice(0, 8).map((item, index) => (
              <CashFlowBar key={`${item.fecha}-${index}`} item={item} max={maxFlujo} />
            ))
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="space-y-3 lg:col-span-2">
          <h2 className="text-lg font-semibold text-primary">Rentabilidad por Operación</h2>
          {data.intereses.length === 0 ? (
            <p className="text-sm text-slate-500">Sin operaciones rentables registradas.</p>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-3">Factura</th>
                    <th className="py-2 pr-3">Tasa</th>
                    <th className="py-2 pr-3">Interés</th>
                    <th className="py-2 pr-3">Comisión</th>
                    <th className="py-2 pr-3">Ganancia</th>
                  </tr>
                </thead>
                <tbody>
                  {data.intereses.slice(0, 8).map((item) => (
                    <tr key={item.factura_id} className="border-b">
                      <td className="py-2 pr-3">#{item.factura_id}</td>
                      <td className="py-2 pr-3">{(Number(item.tasa_interes || 0) * 100).toFixed(2)}%</td>
                      <td className="py-2 pr-3">{money(item.interes_generado)}</td>
                      <td className="py-2 pr-3">{money(item.comision)}</td>
                      <td className="py-2 pr-3 font-semibold text-emerald-700">{money(item.ganancia_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="space-y-3">
          <h2 className="text-lg font-semibold text-primary">Riesgo y Morosidad</h2>
          {data.morosidad.length === 0 ? (
            <p className="text-sm text-slate-500">No hay morosidad detectada.</p>
          ) : (
            data.morosidad.slice(0, 6).map((item) => (
              <div key={item.factura_id} className="border rounded p-3 bg-slate-50">
                <div className="flex justify-between text-sm">
                  <b>Factura #{item.factura_id}</b>
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      item.riesgo === "alto"
                        ? "bg-red-100 text-red-700"
                        : item.riesgo === "medio"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {item.riesgo}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {item.dias_atraso} días de atraso · {money(item.monto)}
                </p>
              </div>
            ))
          )}
        </Card>
      </div>

      <Card className="space-y-3">
        <h2 className="text-lg font-semibold text-primary">Próximos Vencimientos</h2>
        {data.vencimientos.length === 0 ? (
          <p className="text-sm text-slate-500">No hay facturas próximas a vencer.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.vencimientos.slice(0, 6).map((item) => (
              <div key={`${item.factura_id}-${item.fecha_vencimiento}`} className="border rounded p-3 bg-slate-50">
                <p className="font-semibold text-primary">Factura #{item.factura_id}</p>
                <p className="text-sm text-slate-600">{item.deudor}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Vence: {new Date(item.fecha_vencimiento).toLocaleDateString("es-PY")} · {item.dias_restantes} días
                </p>
                <p className="text-sm font-semibold mt-2">{money(item.monto)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
